/**
 * Allergen category vocabulary — shared between dish detail (Dish.allergens)
 * and the user's profile allergy preferences (ChowProfile.allergies). The
 * backend stores free-form lowercase slugs so this list can grow without a
 * schema change; these are the curated categories the UI offers.
 */

export interface AllergenCategory {
  slug: string
  label: string
  glyph: string
}

export const ALLERGEN_CATEGORIES: readonly AllergenCategory[] = [
  { slug: 'peanuts', label: 'Peanuts', glyph: '🥜' },
  { slug: 'tree_nuts', label: 'Tree nuts', glyph: '🌰' },
  { slug: 'dairy', label: 'Dairy', glyph: '🥛' },
  { slug: 'eggs', label: 'Eggs', glyph: '🥚' },
  { slug: 'gluten', label: 'Gluten', glyph: '🌾' },
  { slug: 'soy', label: 'Soy', glyph: '🫘' },
  { slug: 'fish', label: 'Fish', glyph: '🐟' },
  { slug: 'shellfish', label: 'Shellfish', glyph: '🦐' },
  { slug: 'sesame', label: 'Sesame', glyph: '🫙' },
  { slug: 'celery', label: 'Celery', glyph: '🥬' },
  { slug: 'mustard', label: 'Mustard', glyph: '🌭' },
  { slug: 'sulphites', label: 'Sulphites', glyph: '🍷' },
]

const BY_SLUG = new Map(ALLERGEN_CATEGORIES.map((c) => [c.slug, c]))

/** Label an allergen slug; unknown slugs render capitalized as-is. */
export const allergenLabel = (slug: string): string => {
  const known = BY_SLUG.get(slug)
  if (known) return known.label
  const clean = slug.replace(/_/g, ' ').trim()
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}

export const allergenGlyph = (slug: string): string => BY_SLUG.get(slug)?.glyph ?? '⚠️'

/** Dish allergens ∩ profile allergies — what this eater must be warned about. */
export const allergenConflicts = (
  dishAllergens: readonly string[],
  profileAllergies: readonly string[],
): string[] => {
  const avoid = new Set(profileAllergies.map((a) => a.toLowerCase()))
  return dishAllergens.filter((a) => avoid.has(a.toLowerCase()))
}
